import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';

import { useRollup } from '../hooks/use-rollup';
import { canRollup, MAX_ROLLUP_DAYS, type DayRange } from '../schemas/report-range';

export interface RollupBarProps {
  storeId: string;
  range: DayRange;
  /** Freshness of the numbers; left out when the report does not say (sales today). */
  lastRollupAt?: string | null;
}

/**
 * How fresh the numbers are, and an on-demand recompute of the visible range. The backend caps a
 * rollup at 31 days, so longer ranges only show why the button is off.
 */
export function RollupBar({ storeId, range, lastRollupAt }: RollupBarProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const rollup = useRollup(storeId);
  const allowed = canRollup(range);

  return (
    <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
      {lastRollupAt !== undefined ? (
        <span data-testid="rollup-freshness">
          {lastRollupAt ? (
            <span title={format.utc(lastRollupAt)}>
              {t('rollup.asOf', { time: format.dateTime(lastRollupAt) })}
            </span>
          ) : (
            t('rollup.never')
          )}
        </span>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={!allowed || rollup.isPending}
        data-tour="reports.refresh"
        onClick={() => rollup.mutate({ from: range.from, to: range.to })}
        aria-describedby={allowed ? undefined : 'rollup-hint'}
      >
        <RefreshCw className={rollup.isPending ? 'animate-spin' : undefined} aria-hidden />
        {rollup.isPending ? t('rollup.refreshing') : t('rollup.refresh')}
      </Button>
      {allowed ? null : (
        <span id="rollup-hint" className="text-xs">
          {t('rollup.tooLong', { max: MAX_ROLLUP_DAYS })}
        </span>
      )}
    </div>
  );
}
